
import React, { useState, useMemo, useEffect, useRef } from 'react';
import type { Recipe, Language, UserMealPreferences, Chef, User, Role, DayOfWeek, MealType, HouseholdRole, Household } from '../types';
import SearchField from './SearchField';
import HeartIcon from './icons/HeartIcon';
import FilterIcon from './icons/FilterIcon';
import ArrowLeftIcon from './icons/ArrowLeftIcon';
import ChefHatIcon from './icons/ChefHatIcon';
import PlusIcon from './icons/PlusIcon';
import RecipeFilterModal from './RecipeFilterModal';
import RecipeCard from './RecipeCard';
import AddMealModal from './AddMealModal';
import ConfirmationModal from './ConfirmationModal';
import { getTranslation } from '../utils/translations';
import { getActiveChefs } from '../utils/chefDisplay';
import { isFavoriteRecipe } from '../utils/userMealPreferences';
import {
    filterAndSortRecipes,
    countActiveRecipeFilters,
    DEFAULT_RECIPE_FILTERS,
    type RecipeFilterState,
} from '../utils/recipeFilters';

interface RecipeSelectorModalProps {
    isOpen: boolean;
    onClose: () => void;
    recipes: Recipe[];
    title: string;
    targetDay: DayOfWeek;
    targetMealType: MealType;
    weekOffset: number;
    orderedDays: DayOfWeek[];
    visibleMealTypes: MealType[];
    onConfirmSchedule: (
        recipeId: string,
        day: DayOfWeek,
        type: MealType,
        servings: number,
        status: 'buy' | 'pantry' | 'prepared',
        weekOffset: number,
    ) => Promise<void> | void;
    activeHouseholdId?: string;
    language?: Language;
    userMealPreferences?: UserMealPreferences | null;
    currentUserId?: string;
    chefs?: Chef[];
    allUsers?: User[];
    activeHousehold?: Household | null;
    userRole?: Role;
    householdRole?: HouseholdRole;
    onToggleFavoriteRecipe?: (recipeId: string) => void;
    onCreateRecipe?: (name: string) => Promise<string | void>;
}

const RecipeSelectorModal: React.FC<RecipeSelectorModalProps> = ({
    isOpen,
    onClose,
    recipes,
    title,
    targetDay,
    targetMealType,
    weekOffset,
    orderedDays,
    visibleMealTypes,
    onConfirmSchedule,
    activeHouseholdId,
    language,
    userMealPreferences = null,
    currentUserId = '',
    chefs = [],
    allUsers = [],
    activeHousehold = null,
    userRole = 'user',
    householdRole = 'viewer',
    onToggleFavoriteRecipe,
    onCreateRecipe,
}) => {
    const t = (key: Parameters<typeof getTranslation>[1]) => getTranslation(language, key);
    const canManage = userRole === 'superuser' || ['owner', 'admin', 'editor'].includes(householdRole);

    const [searchQuery, setSearchQuery] = useState('');
    const [filters, setFilters] = useState<RecipeFilterState>(DEFAULT_RECIPE_FILTERS);
    const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
    const [scheduleRecipe, setScheduleRecipe] = useState<Recipe | null>(null);
    const [showCreateConfirm, setShowCreateConfirm] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const didScheduleRef = useRef(false);

    useEffect(() => {
        if (!isOpen) {
            setSearchQuery('');
            setScheduleRecipe(null);
            setFilters(DEFAULT_RECIPE_FILTERS);
        }
    }, [isOpen]);

    const activeFilterCount = useMemo(() => countActiveRecipeFilters(filters), [filters]);

    const filteredRecipes = useMemo(() => filterAndSortRecipes(recipes, filters, {
        searchQuery,
        currentUserId,
        activeHouseholdId,
        userMealPreferences,
        chefs,
        allUsers,
    }), [recipes, filters, searchQuery, currentUserId, activeHouseholdId, userMealPreferences, chefs, allUsers]);

    const trimmedSearch = searchQuery.trim();
    const showCreateRecipeButton = Boolean(
        onCreateRecipe && canManage && trimmedSearch.length > 0 && filteredRecipes.length === 0,
    );

    if (!isOpen) return null;

    const handleCloseAll = () => {
        setScheduleRecipe(null);
        setSearchQuery('');
        onClose();
    };

    const handleScheduleConfirm = async (
        recipeId: string,
        day: DayOfWeek,
        type: MealType,
        servings: number,
        status: 'buy' | 'pantry' | 'prepared',
        offset: number,
    ) => {
        await onConfirmSchedule(recipeId, day, type, servings, status, offset);
        didScheduleRef.current = true;
    };

    const handleScheduleClose = () => {
        if (didScheduleRef.current) {
            didScheduleRef.current = false;
            handleCloseAll();
            return;
        }
        setScheduleRecipe(null);
    };

    const handleCreateRecipe = async () => {
        if (!onCreateRecipe || !trimmedSearch) return;
        setIsCreating(true);
        try {
            const newId = await onCreateRecipe(trimmedSearch);
            if (typeof newId === 'string') {
                setScheduleRecipe({
                    id: newId,
                    name: trimmedSearch,
                    ingredients: [],
                    instructions: [],
                    servings: 2,
                    links: [],
                    ownerId: currentUserId,
                    householdId: activeHouseholdId || '',
                    isPublic: false,
                    isTemporary: true,
                    sharedWithHouseholds: [],
                });
            }
        } finally {
            setIsCreating(false);
            setShowCreateConfirm(false);
        }
    };

    if (scheduleRecipe) {
        return (
            <AddMealModal
                isOpen
                onClose={handleScheduleClose}
                recipe={scheduleRecipe}
                initialDay={targetDay}
                initialType={targetMealType}
                weekOffset={weekOffset}
                onConfirm={handleScheduleConfirm}
                language={language}
                orderedDays={orderedDays}
                visibleMealTypes={visibleMealTypes}
            />
        );
    }

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[300] p-4 animate-fade-in">
            <div className="bg-white dark:bg-slate-900 rounded-[2.5rem] shadow-2xl w-full max-w-md flex flex-col max-h-[90vh] overflow-hidden border border-white/20 dark:border-slate-800">
                <div className="flex-shrink-0 p-4 pb-2 space-y-3 border-b border-slate-50 dark:border-slate-800 bg-white dark:bg-slate-900">
                    <h3 className="text-lg font-black text-slate-800 dark:text-slate-100 tracking-tight px-1">{title}</h3>

                    <div className="flex gap-2 items-center">
                        <SearchField
                            className="flex-grow"
                            value={searchQuery}
                            onChange={setSearchQuery}
                            placeholder={t('search')}
                            clearLabel={t('clearSearch')}
                            autoFocus
                        />
                        {showCreateRecipeButton && (
                            <button
                                type="button"
                                onClick={() => setShowCreateConfirm(true)}
                                disabled={isCreating}
                                className="p-3.5 bg-brand-orange text-white rounded-2xl shadow-lg shadow-brand-orange/20 active:scale-95 transition-all flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-orange-600"
                                title={t('quickRecipeCreateTitle')}
                                aria-label={t('quickRecipeCreateTitle')}
                            >
                                <PlusIcon className="w-5 h-5" />
                            </button>
                        )}
                    </div>

                    <div className="flex items-center justify-between gap-2">
                        <button
                            type="button"
                            onClick={handleCloseAll}
                            className="flex items-center justify-center gap-2 h-9 w-9 sm:w-auto px-0 sm:px-4 rounded-full border border-gray-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700 shadow-sm transition-all flex-shrink-0"
                            title={t('backToPlanner')}
                            aria-label={t('backToPlanner')}
                        >
                            <ArrowLeftIcon className="w-4 h-4 shrink-0 rtl:rotate-180" />
                            <span className="hidden sm:inline text-sm font-medium">{t('backToPlanner')}</span>
                        </button>

                        <div className="flex items-center gap-2">
                            <button
                                type="button"
                                onClick={() => setFilters((prev) => ({
                                    ...prev,
                                    visibilitySelections: prev.visibilitySelections.includes('favorites')
                                        ? prev.visibilitySelections.filter((item) => item !== 'favorites')
                                        : [...prev.visibilitySelections, 'favorites'],
                                }))}
                                className={`flex flex-row items-center justify-center gap-2 rounded-full h-9 w-9 sm:w-auto px-0 sm:px-4 border shadow-sm transition-all flex-shrink-0
                                    ${filters.visibilitySelections.includes('favorites')
                                        ? 'border-brand-sage text-brand-sage bg-brand-sage/10 dark:bg-brand-sage/20'
                                        : 'bg-white dark:bg-slate-800 border-gray-300 dark:border-slate-700 text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700'}`}
                                title={t('showFavorites')}
                            >
                                <HeartIcon className={`w-4 h-4 shrink-0 ${filters.visibilitySelections.includes('favorites') ? 'text-brand-sage' : 'text-gray-600 dark:text-slate-300'}`} filled={filters.visibilitySelections.includes('favorites')} />
                                <span className="hidden sm:inline text-sm font-medium">{t('favorites')}</span>
                            </button>

                            <button
                                type="button"
                                onClick={() => setIsFilterModalOpen(true)}
                                className={`
                                    relative flex flex-row items-center justify-center gap-2 rounded-full h-9 min-w-9 sm:min-w-0 px-2 sm:px-4 border shadow-sm transition-all flex-shrink-0
                                    ${activeFilterCount > 0
                                        ? 'border-brand-sage text-brand-sage bg-brand-sage/5 dark:bg-brand-sage/20'
                                        : 'border-gray-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700'}
                                `}
                                title={t('filter')}
                            >
                                <FilterIcon className={`w-4 h-4 shrink-0 ${activeFilterCount > 0 ? 'text-brand-sage' : 'text-gray-600 dark:text-slate-400'}`} />
                                <span className="hidden sm:inline text-sm font-medium">{t('filter')}</span>
                                {activeFilterCount > 0 && (
                                    <span className="absolute -top-1 end-0 sm:static min-w-[1rem] h-4 px-0.5 bg-brand-sage text-white text-[9px] flex items-center justify-center rounded-full font-bold shrink-0 leading-none">
                                        {activeFilterCount}
                                    </span>
                                )}
                            </button>
                        </div>
                    </div>
                </div>

                <div className="flex-grow overflow-y-auto scrollbar-none bg-white dark:bg-slate-900">
                    <div className="flex flex-col">
                        {filteredRecipes.length > 0 ? (
                            filteredRecipes.map(recipe => (
                                <RecipeCard
                                    key={recipe.id}
                                    recipe={recipe}
                                    role={userRole}
                                    canManage={canManage && recipe.householdId === activeHouseholdId}
                                    onEdit={() => {}}
                                    onView={() => setScheduleRecipe(recipe)}
                                    isReorderMode={false}
                                    onReorder={() => {}}
                                    isSelectionMode={false}
                                    isSelected={false}
                                    onToggleSelect={() => {}}
                                    currentUserId={currentUserId}
                                    showPrivateBadge={false}
                                    onSchedule={() => setScheduleRecipe(recipe)}
                                    language={language}
                                    layout="list"
                                    pickerMode
                                    isFavorite={isFavoriteRecipe(userMealPreferences, recipe.id)}
                                    onToggleFavorite={onToggleFavoriteRecipe ? () => onToggleFavoriteRecipe(recipe.id) : undefined}
                                    allUsers={allUsers}
                                    activeHouseholdId={activeHouseholdId}
                                />
                            ))
                        ) : (
                            <div className="flex flex-col items-center justify-center py-20 opacity-30">
                                <ChefHatIcon className="w-16 h-16 mb-4 text-slate-300 dark:text-slate-600" />
                                <p className="font-black text-xs uppercase tracking-[0.2em] text-slate-400 dark:text-slate-500">{t('noResults')}</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <RecipeFilterModal
                isOpen={isFilterModalOpen}
                onClose={() => setIsFilterModalOpen(false)}
                currentFilters={filters}
                onApply={setFilters}
                language={language}
                chefs={getActiveChefs(chefs)}
                recipes={recipes}
                allUsers={allUsers}
                activeHousehold={activeHousehold}
            />

            <ConfirmationModal
                isOpen={showCreateConfirm}
                title={t('quickRecipeCreateTitle')}
                message={t('quickRecipeCreateMessage')}
                confirmText={t('createAndSchedule')}
                cancelText={t('cancel')}
                type="info"
                onConfirm={handleCreateRecipe}
                onCancel={() => setShowCreateConfirm(false)}
            />
        </div>
    );
};

export default RecipeSelectorModal;
